import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from '@ui/primitives/utils';
import { Code, Github, PackageOpen, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
function getCommand(packageManager) {
    switch(packageManager){
        case 'npm':
            return 'npx create-zephyr-apps@latest';
        case 'yarn':
            return 'yarn dlx create-zephyr-apps@latest';
        case 'pnpm':
            return 'pnpm dlx create-zephyr-apps@latest';
        default:
            return 'pnpm dlx create-zephyr-apps@latest';
    }
}
export function EmptyApplicationList(props) {
    var _props_showQuickLinks, _props_title, _props_subtitle;
    const isCli = props.variant === 'cli';
    const [packageManager, setPackageManager] = useState('pnpm');
    const [copied, setCopied] = useState(false);
    const command = useMemo(()=>getCommand(packageManager), [
        packageManager
    ]);
    const handleCopy = async ()=>{
        await navigator.clipboard.writeText(command);
        setCopied(true);
        window.setTimeout(()=>setCopied(false), 1200);
    };
    if (!isCli) {
        var _props_title1;
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex min-h-[200px] flex-col items-center justify-center gap-4 p-8",
            children: [
                /*#__PURE__*/ _jsx(PackageOpen, {
                    className: "h-16 w-16 text-muted-foreground"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-center text-base text-muted-foreground",
                    children: (_props_title1 = props.title) !== null && _props_title1 !== void 0 ? _props_title1 : 'There are currently no applications added. Please start with creating a new project'
                })
            ]
        });
    }
    const showQuickLinks = (_props_showQuickLinks = props.showQuickLinks) !== null && _props_showQuickLinks !== void 0 ? _props_showQuickLinks : true;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex max-w-[900px] flex-col items-start gap-3 m-auto",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "w-full",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "mb-1 text-lg font-semibold text-foreground",
                        children: (_props_title = props.title) !== null && _props_title !== void 0 ? _props_title : 'New App'
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mb-2 text-sm text-muted-foreground",
                        children: (_props_subtitle = props.subtitle) !== null && _props_subtitle !== void 0 ? _props_subtitle : 'It is as easy as building your application with our Zephyr bundler package.'
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mb-3 rounded-lg border border-border bg-card p-5 shadow-sm",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mb-2 flex items-center justify-between gap-3",
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "font-medium text-foreground",
                                        children: "Deploy with CLI"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "inline-flex h-7 gap-0.5 rounded-lg bg-muted p-0.5",
                                                children: [
                                                    'pnpm',
                                                    'npm',
                                                    'yarn'
                                                ].map((pm)=>/*#__PURE__*/ _jsx("button", {
                                                        type: "button",
                                                        onClick: ()=>setPackageManager(pm),
                                                        "aria-label": "package manager ".concat(pm),
                                                        className: cn('h-full rounded px-2 text-xs capitalize transition-colors', packageManager === pm ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:bg-background/60'),
                                                        children: pm
                                                    }, pm))
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: ()=>{
                                                    void handleCopy();
                                                },
                                                className: "h-7 rounded-md border border-border px-2 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                                                children: copied ? 'Copied' : 'Copy'
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "overflow-x-auto rounded-md border border-border bg-muted/30 p-3",
                                children: /*#__PURE__*/ _jsx("code", {
                                    className: "text-sm text-foreground",
                                    children: command
                                })
                            })
                        ]
                    })
                ]
            }),
            showQuickLinks ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "mb-0.5 mt-1 text-[0.7rem] uppercase tracking-[0.05em] text-muted-foreground",
                        children: "Or try one of these options"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex w-full gap-3",
                        children: [
                            /*#__PURE__*/ _jsx("a", {
                                href: "https://docs.zephyr-cloud.io/getting-started/quick-start",
                                target: "_blank",
                                rel: "noopener noreferrer",
                                className: "block h-full w-full rounded-lg p-0 text-left",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "flex h-full flex-col rounded-lg border border-border bg-card p-5 transition-colors duration-200 hover:bg-muted/30",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-3",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Code, {
                                                        className: "size-4 text-emerald-400"
                                                    }),
                                                    /*#__PURE__*/ _jsx("h4", {
                                                        className: "text-sm font-medium text-foreground",
                                                        children: "Start from scratch"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "Build a new application from the ground up"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "mt-auto pt-1",
                                                children: /*#__PURE__*/ _jsx("p", {
                                                    className: "text-sm font-medium text-emerald-400",
                                                    children: "Learn more ->"
                                                })
                                            })
                                        ]
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx("a", {
                                href: "https://github.com/ZephyrCloudIO/zephyr-examples",
                                target: "_blank",
                                rel: "noopener noreferrer",
                                className: "block h-full w-full rounded-lg p-0 text-left",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "flex h-full flex-col rounded-lg border border-border bg-card p-5 transition-colors duration-200 hover:bg-muted/30",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-3",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Github, {
                                                        className: "size-4 text-emerald-400"
                                                    }),
                                                    /*#__PURE__*/ _jsx("h4", {
                                                        className: "text-sm font-medium text-foreground",
                                                        children: "Fork an example"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "Get started with pre-built examples from our repository"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "mt-auto pt-1",
                                                children: /*#__PURE__*/ _jsx("p", {
                                                    className: "text-sm font-medium text-emerald-400",
                                                    children: "View examples ->"
                                                })
                                            })
                                        ]
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx("a", {
                                href: "https://docs.zephyr-cloud.io",
                                target: "_blank",
                                rel: "noopener noreferrer",
                                className: "block h-full w-full rounded-lg p-0 text-left",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "flex h-full flex-col rounded-lg border border-border bg-card p-5 transition-colors duration-200 hover:bg-muted/30",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-3",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Sparkles, {
                                                        className: "size-4 text-emerald-400"
                                                    }),
                                                    /*#__PURE__*/ _jsx("h4", {
                                                        className: "text-sm font-medium text-foreground",
                                                        children: "Read the docs"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "Explore our docs to learn about all Zephyr features"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "mt-auto pt-1",
                                                children: /*#__PURE__*/ _jsx("p", {
                                                    className: "text-sm font-medium text-emerald-400",
                                                    children: "View docs ->"
                                                })
                                            })
                                        ]
                                    })
                                })
                            })
                        ]
                    })
                ]
            }) : null
        ]
    });
}
